<template>
  <div>
    <Teleport to="body">
      <GlobalDialog
        dialog-default-size="55%"
        v-model="showDialog"
        title=""
        :showActionButton="false"
        :force-close="true"
        align-center
      >
        <template #content>
          <div class="flex items-center gap-3 mb-5">
            <div
              class="flex items-center justify-center w-9 h-9 rounded-lg bg-teal-50 flex-shrink-0"
            >
              <CalendarIcon class="w-5 h-5 text-teal-700" />
            </div>
            <div>
              <h5 class="text-lg font-bold text-untitled-gray-900 leading-none">
                Upcoming Events
              </h5>
              <p class="text-xs text-untitled-gray-500 mt-0.5">
                {{ events.length }} event{{ events.length === 1 ? "" : "s" }}
                scheduled
              </p>
            </div>
          </div>

          <ul role="list" class="flex flex-col gap-3 max-h-[60vh] overflow-y-auto pr-1">
            <li v-for="event in events" :key="event.id">
              <div
                class="group flex items-center gap-4 p-3 rounded-xl border border-untitled-gray-200 hover:border-teal-300 hover:bg-teal-50/40 transition-all duration-200 cursor-pointer"
                @click="viewEvent(event.id)"
              >
                <div
                  class="flex-shrink-0 flex flex-col items-center justify-center w-16 h-16 rounded-lg bg-teal-800 text-white"
                >
                  <span class="text-[10px] font-semibold uppercase tracking-wide">
                    {{ formatMonth(event.start_at) }}
                  </span>
                  <span class="text-xl font-bold leading-none">
                    {{ formatDay(event.start_at) }}
                  </span>
                </div>

                <div class="flex-1 min-w-0">
                  <h4 class="text-sm font-semibold text-untitled-gray-900 truncate">
                    {{ event.name }}
                  </h4>
                  <p class="text-xs text-untitled-gray-500 line-clamp-2 mt-0.5">
                    {{
                      event.description
                        ? event.description
                        : "No description available"
                    }}
                  </p>
                  <div class="flex items-center gap-1 mt-1.5 text-xs text-untitled-gray-500">
                    <ClockIcon class="w-3.5 h-3.5 text-untitled-gray-400" />
                    <span>
                      {{ formatDate(event.start_at, event.end_at) }} ·
                      {{ formatTime(event.start_at) }}
                    </span>
                  </div>
                </div>

                <button
                  type="button"
                  class="flex-shrink-0 inline-flex items-center gap-0.5 text-xs font-semibold text-teal-800 group-hover:text-teal-900"
                  @click.stop="viewEvent(event.id)"
                >
                  View
                  <ChevronRightIcon class="w-4 h-4" />
                </button>
              </div>
            </li>
          </ul>
        </template>
      </GlobalDialog>
    </Teleport>
  </div>
</template>

<script setup>
import { onMounted, ref, inject } from "vue";
import GlobalDialog from "@/components/layout/GlobalDialog.vue";
import { isEmpty as _isEmpty } from "lodash-es";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import { useDialogStore } from "@/stores/useDialogStore";
import { useRouter } from "vue-router";
import { CalendarIcon, ClockIcon, ChevronRightIcon } from "@heroicons/vue/solid";

dayjs.extend(utc);

const dialogStore = useDialogStore();
const router = useRouter();

const showDialog = ref(false);
const axios = inject("axios");
const events = ref([]);

const SHOWN_KEY = "upcoming_events_modal_shown";

const formatDate = (start_date, end_date) => {
  let start = dayjs(start_date).format("MMM D");
  let end = dayjs(end_date).format("MMM D");

  if (start === dayjs().format("MMM D")) {
    return "Today";
  }

  if (start === end) {
    return start;
  }

  return `${start} - ${end}`;
};

const formatTime = (value) => {
  return dayjs.utc(value).format("h:mm A");
};

const formatMonth = (value) => {
  return dayjs(value).format("MMM");
};

const formatDay = (value) => {
  return dayjs(value).format("D");
};

const viewEvent = (id) => {
  router.push(`/events?eventId=${id}`);
};
const getEvents = () => {
  axios
    .get("/api/event/incoming-events", {
      params: {
        user_time_zone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      },
    })
    .then(({ data }) => {
      if (_isEmpty(data)) {
        return;
      }

      if (!dialogStore.oneTimeVisible) {
        showDialog.value = true;
        events.value = data;
        dialogStore.isDialogVisible = true;
        dialogStore.oneTimeVisible = true;
        sessionStorage.setItem(SHOWN_KEY, "1");
      }
    });
};
onMounted(async () => {
  if (sessionStorage.getItem(SHOWN_KEY) === "1") {
    return;
  }

  getEvents();
});
</script>
